import { useEffect, useRef, useState } from 'react';
import type { SpeciesId } from '../../types.ts';
import { Sprite } from '../Sprite.tsx';
import type { BuilderSpecies } from './builder-data.ts';
import type { EditorMember } from './serialize.ts';
import { SpeciesPicker } from './SpeciesPicker.tsx';

// Convert a base species id ("froslass") into the Pikalytics display name
// ("Froslass") — Pikalytics rows are keyed by display name; the species
// picker hands us BuilderSpecies which has both id (lowercase) and name
// (title-cased). When adding to the rail's defender/speed lists, we use
// `name` directly. Always-mega species like Charizard-Mega-Y won't be in
// the base catalog (those are in pikalytics under that display name too,
// but the picker only shows base species today — accepted gap).

type Source = 'pikalytics' | 'showdown' | 'tournaments';
type DaysWindow = 7 | 30 | 90 | null;

// localStorage keys. Per-regulation namespace because Reg M-B (when it
// arrives) will have different defaults.
const STORAGE_KEY = (kind: string, reg: string) => `vgcbuddy.builder.rail.${kind}.${reg}`;

function loadList(kind: 'defenders' | 'speed', reg: string): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY(kind, reg));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}
function saveList(kind: 'defenders' | 'speed', reg: string, list: string[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY(kind, reg), JSON.stringify(list));
  } catch {
    // Storage quota / disabled — silently no-op; the in-memory state still
    // works for the current session.
  }
}
function loadSource(reg: string): Source {
  const v = window.localStorage.getItem(STORAGE_KEY('source', reg));
  return v === 'showdown' || v === 'tournaments' ? v : 'pikalytics';
}
function loadDays(reg: string): DaysWindow {
  const v = Number.parseInt(window.localStorage.getItem(STORAGE_KEY('days', reg)) ?? '', 10);
  return v === 7 || v === 30 || v === 90 ? v : null;
}
function saveScalar(kind: string, reg: string, value: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY(kind, reg), value);
  } catch {
    // no-op
  }
}

// Right-side decision rail for the active mon: damage panel on top
// (this mon → top 5 meta with the user's first selected move), speed tier
// panel below (where the active mon lands in the meta speed ladder).
//
// Recomputes on member change with a debounce so EV slider drags don't fire
// a request per pixel. The damage panel sticks to the FIRST non-empty move
// in the editor; iterating across all four moves would 4× the request
// volume for marginal value — the user can flip the row's move toggle
// in chat or in a future per-row variant.

type DamageCell = {
  move: string;
  min_pct: number;
  max_pct: number;
  ko_text: string;
  ko_chance: number;
  is_ohko: boolean;
  variant: 'standard' | 'super' | 'resisted' | 'immune';
} | null;

type RailPayload = {
  ok: boolean;
  damage_rows: Array<{
    defender_species: string;
    defender_label: string;
    damage: DamageCell;
    unavailable_reason: string | null;
  }>;
  incoming_rows: Array<{
    attacker_species: string;
    attacker_label: string;
    damage: DamageCell;
    unavailable_reason: string | null;
  }>;
  speed_tier: {
    rows: Array<{
      species: string;
      final_speed: number;
      outpaces_me: boolean;
      usage_pct: number;
    }>;
    my_species?: string;
    my_speed?: number;
  };
};

export function ContextRail({
  member,
  regulationId = 'M-A',
}: {
  member: EditorMember | null;
  regulationId?: string;
}) {
  const [data, setData] = useState<RailPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Debounce ref — clear/set so rapid EV changes only fire one request after
  // the dust settles.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // User-customised defender + speed-comparison lists. Empty array means
  // "use server defaults". Persisted per regulation.
  const [defenders, setDefenders] = useState<string[]>(() => loadList('defenders', regulationId));
  const [speedExtras, setSpeedExtras] = useState<string[]>(() => loadList('speed', regulationId));
  // Source + time-window for the "default top species" ranking. Drives
  // BOTH the damage defenders (when no custom list) AND the speed tier's
  // species set. Persisted across reloads.
  const [source, setSource] = useState<Source>(() => loadSource(regulationId));
  const [days, setDays] = useState<DaysWindow>(() => loadDays(regulationId));
  // Picker overlay state — which list is currently being edited.
  const [pickerFor, setPickerFor] = useState<null | 'defenders' | 'speed'>(null);

  useEffect(() => {
    if (!member?.species) {
      setData(null);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      setErr(null);
      // Send all non-empty moves; the server picks the best per defender.
      // Falls back to first move only if the user has just one filled in.
      const moveList = (member.moves ?? []).filter((m) => m && m.trim().length > 0);
      fetch('/api/builder/rail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          regulation_id: regulationId,
          attacker: {
            species: member.species,
            item: member.item,
            ability: member.ability,
            nature: member.nature,
            evs: member.evs,
            ivs: member.ivs,
          },
          moves: moveList,
          top_n: 10,
          defenders: defenders.length > 0 ? defenders : undefined,
          speed_extras: speedExtras.length > 0 ? speedExtras : undefined,
          source,
          days,
        }),
        signal: ctrl.signal,
      })
        .then((r) => r.json())
        .then((body) => setData(body as RailPayload))
        .catch((e: unknown) => {
          if ((e as Error)?.name !== 'AbortError') {
            setErr(String((e as Error)?.message ?? e));
          }
        })
        .finally(() => setLoading(false));
    }, 120);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [
    member?.species,
    member?.item,
    member?.ability,
    member?.nature,
    member?.evs?.hp,
    member?.evs?.atk,
    member?.evs?.def,
    member?.evs?.spa,
    member?.evs?.spd,
    member?.evs?.spe,
    member?.moves?.[0],
    member?.moves?.[1],
    member?.moves?.[2],
    member?.moves?.[3],
    regulationId,
    // Refetch when the user's custom lists change. Stringifying for stable
    // dep comparison — arrays are reference-unstable.
    defenders.join('|'),
    speedExtras.join('|'),
    source,
    days,
  ]);

  // Helpers for the +/× controls. Each mutates the appropriate list AND
  // persists to localStorage so the user's preferences survive refresh.
  const addDefender = (species: BuilderSpecies) => {
    setDefenders((prev) => {
      if (prev.includes(species.name)) return prev;
      const next = [...prev, species.name];
      saveList('defenders', regulationId, next);
      return next;
    });
    setPickerFor(null);
  };
  const removeDefender = (name: string) => {
    setDefenders((prev) => {
      const next = prev.filter((n) => n !== name);
      saveList('defenders', regulationId, next);
      return next;
    });
  };
  const resetDefenders = () => {
    setDefenders([]);
    saveList('defenders', regulationId, []);
  };
  const addSpeed = (species: BuilderSpecies) => {
    setSpeedExtras((prev) => {
      if (prev.includes(species.name)) return prev;
      const next = [...prev, species.name];
      saveList('speed', regulationId, next);
      return next;
    });
    setPickerFor(null);
  };
  const removeSpeed = (name: string) => {
    setSpeedExtras((prev) => {
      const next = prev.filter((n) => n !== name);
      saveList('speed', regulationId, next);
      return next;
    });
  };

  if (!member) {
    return (
      <div
        className="vb-card"
        style={{
          padding: 24,
          textAlign: 'center',
          color: 'var(--fg-4)',
          fontSize: 11,
        }}
      >
        Speed tier &amp; damage — pick a Pokémon for this slot.
      </div>
    );
  }

  const customDefendersActive = defenders.length > 0;
  const updateSource = (next: Source) => {
    setSource(next);
    saveScalar('source', regulationId, next);
  };
  const updateDays = (next: DaysWindow) => {
    setDays(next);
    saveScalar('days', regulationId, next === null ? '' : String(next));
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <SourceBar
        source={source}
        days={days}
        onSource={updateSource}
        onDays={updateDays}
        disabled={customDefendersActive}
      />
      <DamagePanel
        data={data}
        loading={loading}
        err={err}
        customActive={customDefendersActive}
        onAdd={() => setPickerFor('defenders')}
        onRemove={removeDefender}
        onReset={resetDefenders}
      />
      <IncomingPanel data={data} loading={loading} />
      <SpeedPanel
        data={data}
        loading={loading}
        extras={speedExtras}
        onAdd={() => setPickerFor('speed')}
        onRemove={removeSpeed}
      />
      <SpeciesPicker
        open={pickerFor !== null}
        onClose={() => setPickerFor(null)}
        onPick={(s) => (pickerFor === 'defenders' ? addDefender(s) : addSpeed(s))}
        regulationId={regulationId}
        // Rail defenders/speed-extras can be mega forms (you want to pin
        // "Aerodactyl-Mega" as a separate damage row from base Aero).
        includeMegas
      />
    </div>
  );
}

function DamagePanel({
  data,
  loading,
  err,
  customActive,
  onAdd,
  onRemove,
  onReset,
}: {
  data: RailPayload | null;
  loading: boolean;
  err: string | null;
  customActive: boolean;
  onAdd: () => void;
  onRemove: (name: string) => void;
  onReset: () => void;
}) {
  return (
    <div className="vb-card" style={{ overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          borderBottom: '1px solid var(--line-1)',
          gap: 8,
        }}
      >
        <span
          className="caps mono"
          style={{ fontSize: 9, color: 'var(--fg-3)', letterSpacing: '0.08em' }}
        >
          damage · this mon → {customActive ? 'your picks' : 'top 5'}
        </span>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {loading && (
            <span className="mono" style={{ fontSize: 9, color: 'var(--fg-4)' }}>
              …
            </span>
          )}
          {customActive && (
            <button
              type="button"
              onClick={onReset}
              className="caps mono"
              style={{
                fontSize: 8.5,
                padding: '2px 6px',
                background: 'transparent',
                color: 'var(--fg-4)',
                border: '1px solid var(--line-1)',
                borderRadius: 2,
                letterSpacing: '0.06em',
                cursor: 'pointer',
              }}
              title="Restore top-5-by-usage default"
            >
              reset
            </button>
          )}
          <button
            type="button"
            onClick={onAdd}
            className="caps mono"
            style={{
              fontSize: 8.5,
              padding: '2px 7px',
              background: 'var(--accent-bg)',
              color: 'var(--accent-2)',
              border: '1px solid var(--accent-line)',
              borderRadius: 2,
              letterSpacing: '0.06em',
              cursor: 'pointer',
            }}
            title="Add a custom defender"
          >
            + add
          </button>
        </div>
      </div>
      {err ? (
        <div style={{ padding: 10, color: 'var(--neg)', fontSize: 10.5 }}>{err}</div>
      ) : !data ? (
        <div className="mono" style={{ padding: 10, color: 'var(--fg-4)', fontSize: 10.5 }}>
          {loading ? 'computing…' : 'pick a move to see damage'}
        </div>
      ) : data.damage_rows.length === 0 ? (
        <div className="mono" style={{ padding: 10, color: 'var(--fg-4)', fontSize: 10.5 }}>
          no defenders — tap "+ add" or "reset" to restore the top 5
        </div>
      ) : (
        data.damage_rows.map((row) => (
          <DamageRow
            key={row.defender_species}
            row={row}
            removable={customActive}
            onRemove={() => onRemove(row.defender_species)}
          />
        ))
      )}
    </div>
  );
}

// Defence panel: each top opponent attacks YOU with their best Pikalytics-seen
// move. Red = OHKO threat; warn = ≥50%; green = comfortably survives. Answers
// "do I survive the worst the meta will throw at me?" at a glance.
function IncomingPanel({ data, loading }: { data: RailPayload | null; loading: boolean }) {
  return (
    <div className="vb-card" style={{ overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          borderBottom: '1px solid var(--line-1)',
        }}
      >
        <span
          className="caps mono"
          style={{ fontSize: 9, color: 'var(--fg-3)', letterSpacing: '0.08em' }}
        >
          incoming · top → this mon
        </span>
        {loading && (
          <span className="mono" style={{ fontSize: 9, color: 'var(--fg-4)' }}>
            …
          </span>
        )}
      </div>
      {!data ? (
        <div className="mono" style={{ padding: 10, color: 'var(--fg-4)', fontSize: 10.5 }}>
          {loading ? 'computing…' : '—'}
        </div>
      ) : data.incoming_rows.length === 0 ? (
        <div className="mono" style={{ padding: 10, color: 'var(--fg-4)', fontSize: 10.5 }}>
          no incoming threats (no Pikalytics moves for these species)
        </div>
      ) : (
        data.incoming_rows.map((row) => <IncomingRow key={row.attacker_species} row={row} />)
      )}
    </div>
  );
}

function IncomingRow({ row }: { row: RailPayload['incoming_rows'][number] }) {
  const cell = row.damage;
  const color =
    cell?.is_ohko || (cell && cell.max_pct >= 75)
      ? 'var(--neg)'
      : cell && cell.max_pct >= 50
        ? 'var(--warn)'
        : cell && cell.max_pct <= 25
          ? 'var(--pos)'
          : 'var(--fg-2)';
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '22px 1fr auto',
        gap: 8,
        alignItems: 'center',
        padding: '5px 10px',
        borderBottom: '1px solid var(--line-1)',
      }}
    >
      <Sprite species={row.attacker_species.toLowerCase() as SpeciesId} size={22} />
      <span
        className="mono"
        style={{
          fontSize: 10,
          color: 'var(--fg-2)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {row.attacker_label}
        {cell && <span style={{ color: 'var(--fg-4)' }}> · {cell.move}</span>}
      </span>
      <span
        className="mono tabular"
        style={{ fontSize: 11, color, fontWeight: 500, whiteSpace: 'nowrap' }}
        title={cell?.ko_text ?? row.unavailable_reason ?? 'no calc'}
      >
        {cell ? `${cell.min_pct.toFixed(0)}–${cell.max_pct.toFixed(0)}%` : '—'}
      </span>
    </div>
  );
}

function DamageRow({
  row,
  removable,
  onRemove,
}: {
  row: RailPayload['damage_rows'][number];
  removable: boolean;
  onRemove: () => void;
}) {
  const cell = row.damage;
  const color =
    cell?.is_ohko || (cell && cell.max_pct >= 75)
      ? 'var(--neg)'
      : cell && cell.max_pct >= 50
        ? 'var(--warn)'
        : cell && cell.variant === 'resisted'
          ? 'var(--pos)'
          : 'var(--fg-2)';
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `22px 1fr auto${removable ? ' 22px' : ''}`,
        gap: 8,
        alignItems: 'center',
        padding: '5px 10px',
        borderBottom: '1px solid var(--line-1)',
      }}
    >
      <Sprite species={row.defender_species.toLowerCase() as SpeciesId} size={22} />
      <span
        className="mono"
        style={{
          fontSize: 10,
          color: 'var(--fg-2)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {row.defender_label}
        {cell && <span style={{ color: 'var(--fg-4)' }}> · {cell.move}</span>}
      </span>
      <span
        className="mono tabular"
        style={{ fontSize: 11, color, fontWeight: 500, whiteSpace: 'nowrap' }}
        title={
          row.unavailable_reason === 'no_move'
            ? 'pick a move on the active slot to see damage'
            : (cell?.ko_text ?? 'no calc')
        }
      >
        {cell ? `${cell.min_pct.toFixed(0)}–${cell.max_pct.toFixed(0)}%` : '—'}
      </span>
      {removable && (
        <button
          type="button"
          onClick={onRemove}
          className="mono"
          style={{
            fontSize: 11,
            color: 'var(--fg-4)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
          title={`Remove ${row.defender_species}`}
        >
          ×
        </button>
      )}
    </div>
  );
}

function SpeedPanel({
  data,
  loading,
  extras,
  onAdd,
  onRemove,
}: {
  data: RailPayload | null;
  loading: boolean;
  extras: string[];
  onAdd: () => void;
  onRemove: (name: string) => void;
}) {
  const extrasSet = new Set(extras);
  return (
    <div className="vb-card" style={{ overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          borderBottom: '1px solid var(--line-1)',
          gap: 8,
        }}
      >
        <span
          className="caps mono"
          style={{ fontSize: 9, color: 'var(--fg-3)', letterSpacing: '0.08em' }}
        >
          speed tier
        </span>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {loading && (
            <span className="mono" style={{ fontSize: 9, color: 'var(--fg-4)' }}>
              …
            </span>
          )}
          <button
            type="button"
            onClick={onAdd}
            className="caps mono"
            style={{
              fontSize: 8.5,
              padding: '2px 7px',
              background: 'var(--accent-bg)',
              color: 'var(--accent-2)',
              border: '1px solid var(--accent-line)',
              borderRadius: 2,
              letterSpacing: '0.06em',
              cursor: 'pointer',
            }}
            title="Pin a species into the speed comparison"
          >
            + add
          </button>
        </div>
      </div>
      {!data ? (
        <div className="mono" style={{ padding: 10, color: 'var(--fg-4)', fontSize: 10.5 }}>
          {loading ? 'computing…' : '—'}
        </div>
      ) : (
        (() => {
          // Insert the user's row into the speed tier list (sorted by
          // final_speed desc). If we don't know the user's species (no
          // base_stats lookup) we just render the tier without their row.
          const rows: Array<{
            species: string;
            final_speed: number;
            outpaces_me: boolean;
            usage_pct: number;
            you?: boolean;
          }> = data.speed_tier.rows.map((r) => ({ ...r, you: false }));
          if (data.speed_tier.my_species && typeof data.speed_tier.my_speed === 'number') {
            rows.push({
              species: data.speed_tier.my_species,
              final_speed: data.speed_tier.my_speed,
              outpaces_me: false,
              usage_pct: 0,
              you: true,
            });
            rows.sort((a, b) => b.final_speed - a.final_speed);
          }
          return rows.slice(0, 15 + extras.length).map((r, i) => {
            const isExtra = extrasSet.has(r.species);
            return (
              <div
                key={`${r.species}-${i}-${r.you ? 'you' : 'them'}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '18px 22px 1fr auto auto 18px',
                  gap: 8,
                  alignItems: 'center',
                  padding: '4px 10px',
                  background: r.you ? 'var(--accent-bg)' : 'transparent',
                  borderLeft: `2px solid ${r.you ? 'var(--accent-line)' : 'transparent'}`,
                  borderBottom: '1px solid var(--line-1)',
                }}
              >
                <span className="mono" style={{ fontSize: 9, color: 'var(--fg-4)' }}>
                  {i + 1}
                </span>
                <Sprite species={r.species.toLowerCase() as SpeciesId} size={18} />
                <span
                  style={{
                    fontSize: 10.5,
                    color: 'var(--fg-1)',
                    fontWeight: r.you ? 600 : 400,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {r.you ? 'You · ' : ''}
                  {r.species}
                  {isExtra && (
                    <span
                      className="caps mono"
                      style={{
                        fontSize: 7.5,
                        color: 'var(--accent-2)',
                        marginLeft: 4,
                        letterSpacing: '0.06em',
                      }}
                    >
                      pinned
                    </span>
                  )}
                </span>
                <span className="mono tabular" style={{ fontSize: 10, color: 'var(--fg-2)' }}>
                  {r.final_speed}
                </span>
                <span
                  className="mono"
                  style={{
                    fontSize: 10,
                    color: r.you ? 'var(--accent-2)' : r.outpaces_me ? 'var(--neg)' : 'var(--pos)',
                    width: 12,
                    textAlign: 'center',
                  }}
                >
                  {r.you ? '·' : r.outpaces_me ? '▲' : '▼'}
                </span>
                {isExtra ? (
                  <button
                    type="button"
                    onClick={() => onRemove(r.species)}
                    className="mono"
                    style={{
                      fontSize: 10,
                      color: 'var(--fg-4)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                    title={`Unpin ${r.species}`}
                  >
                    ×
                  </button>
                ) : (
                  <span />
                )}
              </div>
            );
          });
        })()
      )}
    </div>
  );
}

// Source + time-window chip bar — applies to BOTH panels' default rankings
// (damage defenders + speed tier). When a custom defender list is active
// the damage side ignores the source filter (the user's pinned list wins),
// but the chips stay enabled because they still affect the speed tier.
function SourceBar({
  source,
  days,
  onSource,
  onDays,
  disabled,
}: {
  source: Source;
  days: DaysWindow;
  onSource: (s: Source) => void;
  onDays: (d: DaysWindow) => void;
  disabled: boolean;
}) {
  return (
    <div
      className="vb-card"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        alignItems: 'center',
        padding: '8px 10px',
        opacity: disabled ? 0.7 : 1,
      }}
    >
      <span
        className="caps mono"
        style={{
          fontSize: 9,
          color: 'var(--fg-4)',
          letterSpacing: '0.08em',
          marginRight: 4,
        }}
        title={
          disabled
            ? 'custom defender list is active — source affects speed tier only'
            : 'source for the default top-species ranking'
        }
      >
        source
      </span>
      <SourceChip active={source === 'pikalytics'} onClick={() => onSource('pikalytics')}>
        pikalytics
      </SourceChip>
      <SourceChip active={source === 'showdown'} onClick={() => onSource('showdown')}>
        showdown
      </SourceChip>
      <SourceChip active={source === 'tournaments'} onClick={() => onSource('tournaments')}>
        tournaments
      </SourceChip>
      <span style={{ width: 1, height: 14, background: 'var(--line-1)', margin: '0 2px' }} />
      <span
        className="caps mono"
        style={{
          fontSize: 9,
          color: 'var(--fg-4)',
          letterSpacing: '0.08em',
          marginRight: 4,
        }}
      >
        window
      </span>
      <SourceChip active={days === 7} onClick={() => onDays(7)}>
        7d
      </SourceChip>
      <SourceChip active={days === 30} onClick={() => onDays(30)}>
        30d
      </SourceChip>
      <SourceChip active={days === 90} onClick={() => onDays(90)}>
        90d
      </SourceChip>
      <SourceChip active={days === null} onClick={() => onDays(null)}>
        all
      </SourceChip>
    </div>
  );
}

function SourceChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="caps mono"
      style={{
        fontSize: 8.5,
        padding: '3px 8px',
        background: active ? 'var(--accent-bg)' : 'transparent',
        color: active ? 'var(--accent-2)' : 'var(--fg-3)',
        border: `1px solid ${active ? 'var(--accent-line)' : 'var(--line-1)'}`,
        borderRadius: 2,
        letterSpacing: '0.06em',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}
